/**
 * Data hooks for the three admin consoles. Each fetches its surfaces as one
 * coordinated load, aborts a superseded request, exposes a `reload()` for the
 * error-state retry, and collapses to a single discriminated state the screen
 * renders from — the same shape the Dashboard's `useDashboard` established.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/errors';
import type {
  DlqMessageView,
  EntityStatus,
  PlatformConfigHistoryResponse,
  PlatformConfigView,
  Partner,
  QueueHealthResponse,
  TenantOverview,
  TenantView,
} from '../../api/schema';
import { useSession } from '../../auth/session';

import { DLQ_STAGES, useAdminApi, type AdminApi, type DlqStage } from './api';

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem as Record<string, unknown>;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Generic loading/error/ready state used by every console. */
export type ConsoleState<T> =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | { status: 'ready'; data: T; reload: () => void };

/** Re-run the load whenever `reload()` is called (bumps a nonce). */
function useReloadable<T>(
  run: (api: AdminApi, signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): ConsoleState<T> {
  const api = useAdminApi();
  const [state, setState] = useState<ConsoleState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const data = await run(api, signal);
        if (signal.aborted) return;
        setState({ status: 'ready', data, reload });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();
    return () => {
      controller.abort();
    };
    // `run`/`deps` are provided by the caller; nonce forces a manual reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, reload, nonce, ...deps]);

  return state;
}

// -------------------------------------------------------------- partner console

/** One merged row of the tenants table: identity from the tenant, metrics from the overview. */
export interface TenantRow {
  id: string;
  name: string;
  status: EntityStatus;
  plan: string;
  documents: number;
  reviewBacklog: number;
  failed: number;
  health: string;
}

export interface PartnerConsoleData {
  partner: Partner | null;
  rows: TenantRow[];
  totalTenants: number;
  totalDocuments: number;
  needsAttention: number;
  suspendedCount: number;
}

function mergeRows(tenants: TenantView[], overview: TenantOverview[]): TenantRow[] {
  const byId = new Map(overview.map((o) => [o.tenant_id, o]));
  return tenants.map((t) => {
    const o = byId.get(t.id);
    return {
      id: t.id,
      name: t.name,
      status: t.status ?? 'active',
      plan: t.plan ?? 'standard',
      documents: o?.documents ?? 0,
      reviewBacklog: o?.review_backlog ?? 0,
      failed: o?.failed ?? 0,
      health: o?.health ?? 'ok',
    };
  });
}

/**
 * Resolve the caller's own partner: their own for a partner_admin (a principal
 * that carries a partner reads *that* partner directly — listing partners is
 * `partners:read`, system_admin only, so a partner_admin asking for the list
 * would get a 403), else the first partner for a system_admin.
 */
async function resolveOwnPartner(
  api: AdminApi,
  preferredPartner: string | null,
  signal: AbortSignal,
): Promise<Partner | null> {
  return preferredPartner !== null
    ? await api.partners.get(preferredPartner, signal)
    : ((await api.partners.list({ limit: 50 }, signal)).items[0] ?? null);
}

/** The caller's own partner, alone — for screens that need only the id (e.g.
 * the document-field-visibility settings page), not the full console load. */
export function useOwnPartner(): ConsoleState<Partner | null> {
  const { principal } = useSession();
  const preferredPartner = principal?.partner_id ?? null;

  return useReloadable<Partner | null>(
    (api, signal) => resolveOwnPartner(api, preferredPartner, signal),
    [preferredPartner],
  );
}

/**
 * The partner console load: the caller's partner, its tenants and the
 * cross-tenant overview, merged into table rows and aggregate KPIs.
 */
export function usePartnerConsole(): ConsoleState<PartnerConsoleData> {
  const { principal } = useSession();
  const preferredPartner = principal?.partner_id ?? null;

  return useReloadable<PartnerConsoleData>(
    async (api, signal) => {
      const partner = await resolveOwnPartner(api, preferredPartner, signal);

      if (partner === null) {
        return {
          partner: null,
          rows: [],
          totalTenants: 0,
          totalDocuments: 0,
          needsAttention: 0,
          suspendedCount: 0,
        };
      }

      const [tenantsPage, overview] = await Promise.all([
        api.tenants.list({ partner_id: partner.id, limit: 50 }, signal),
        api.partners.tenantsOverview(partner.id, signal),
      ]);

      const rows = mergeRows(tenantsPage.items, overview.tenants);
      const totalDocuments = overview.tenants.reduce((sum, t) => sum + t.documents, 0);
      const suspendedCount = rows.filter((r) => r.status !== 'active').length;
      const needsAttention = rows.filter(
        (r) => r.status !== 'active' || r.failed > 0 || r.reviewBacklog > 20,
      ).length;

      return {
        partner,
        rows,
        totalTenants: tenantsPage.total,
        totalDocuments,
        needsAttention,
        suspendedCount,
      };
    },
    [preferredPartner],
  );
}

// ---------------------------------------------------------------- system admin

export interface SystemAdminData {
  partners: Partner[];
  config: PlatformConfigView;
  history: PlatformConfigHistoryResponse['versions'];
}

/** The system-admin load: partners, the active config and its version history. */
export function useSystemAdmin(): ConsoleState<SystemAdminData> {
  return useReloadable<SystemAdminData>(async (api, signal) => {
    const [partnersPage, config, history] = await Promise.all([
      api.partners.list({ limit: 50 }, signal),
      api.config.active(signal),
      api.config.history(signal),
    ]);
    return { partners: partnersPage.items, config, history: history.versions };
  }, []);
}

// ----------------------------------------------------------------- processing health

export interface HealthData {
  health: QueueHealthResponse;
  dlq: DlqMessageView[];
}

/** The stages that currently hold dead-lettered messages, from a health snapshot. */
function backloggedStages(health: QueueHealthResponse): DlqStage[] {
  const withDepth = new Set(
    health.dead_letter_queues
      .filter((q) => Number(q.depth ?? 0) > 0)
      .map((q) => String(q.stage)),
  );
  return DLQ_STAGES.filter((s) => withDepth.has(s));
}

/**
 * The processing-health load: the queue/DLQ snapshot, then the failed-message
 * detail for every stage that currently has any — concatenated into one table.
 */
export function useHealth(): ConsoleState<HealthData> {
  return useReloadable<HealthData>(async (api, signal) => {
    const health = await api.health(signal);
    const stages = backloggedStages(health);
    const lists = await Promise.all(stages.map((s) => api.dlq.list(s, signal)));
    const dlq = lists.flatMap((l) => l.messages);
    return { health, dlq };
  }, []);
}

export { backloggedStages };
